import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { type QuickFilterChipConfig, QuickFilterChips } from '@client/components/_ui/data-entry/QuickFilterChips';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { message } from '@client/components/_ui/feedback/message';
import { isWhereClauseEmpty } from '@client/components/filters/core/where-builder';
import { UserFilters } from '@client/components/users/UserFilters';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, Badge, Card, Switch, Typography } from 'antd';
import { Ban, Check, Plug } from 'lucide-react';
import { useState } from 'react';
import { match } from 'ts-pattern';

export const Route = createFileRoute('/console/$orgId/configuration/api-integrations/mcp-access')({
    staticData: {
        presentation: 'page',
        breadcrumb: { title: 'MCP access' },
        ui: {
            icon: Plug,
            title: 'MCP access',
            subtitle: 'Control which users can connect AI assistants to this organisation over MCP',
            menuDescription: 'Control MCP access for your organisation and users',
        },
        permissions: ['mcp_access:manage'],
        featureFlag: 'enable-mcp-access-management',
    },
    component: McpAccessPage,
});

function McpAccessPage() {
    return (
        <PageController route={Route}>
            <McpAccessContent />
        </PageController>
    );
}

function McpAccessContent() {
    const { trpc } = useTrpc();
    const configQuery = trpc.mcpAccess.getOrgConfig.useQuery(undefined);

    if (configQuery.error) {
        return <InlineQueryError error={configQuery.error} onRetry={() => void configQuery.refetch()} />;
    }

    if (configQuery.isLoading || !configQuery.data) {
        return <Loading />;
    }

    const config = configQuery.data;

    return (
        <div className="space-y-4">
            <OrgToggleCard
                orgEnabled={config.orgEnabled}
                systemOverride={config.systemOverride}
                systemOverrideLocked={config.systemOverrideLocked}
            />
            <UserAccessTable orgEffective={config.orgEffective} />
        </div>
    );
}

function OrgToggleCard({
    orgEnabled,
    systemOverride,
    systemOverrideLocked,
}: {
    orgEnabled: boolean;
    systemOverride: string;
    systemOverrideLocked: boolean;
}) {
    const { trpc, trpcUtils } = useTrpc();
    const mutation = trpc.mcpAccess.setOrgEnabled.useMutation({
        async onSuccess(_data, variables) {
            message.success(
                variables.enabled
                    ? 'MCP access enabled for the organisation.'
                    : 'MCP access disabled for the organisation.',
            );
            await Promise.all([
                trpcUtils.mcpAccess.getOrgConfig.invalidate(),
                trpcUtils.mcpAccess.getUserAccessList.invalidate(),
            ]);
        },
        onError(error) {
            errorMessage.show(error);
        },
    });

    return (
        <Card size="small">
            {systemOverrideLocked && (
                <Alert
                    type="info"
                    showIcon
                    className="mb-4"
                    message={`MCP access is currently forced ${
                        systemOverride === 'ON' ? 'on' : 'off'
                    } for your organisation by your platform administrator. The organisation toggle below is read-only.`}
                />
            )}
            <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1">
                    <Typography.Text strong>Enable MCP access for this organisation</Typography.Text>
                    <Typography.Text type="secondary">
                        When enabled, users you grant access to below can connect AI assistants (such as Claude) to
                        Curium over MCP.
                    </Typography.Text>
                </div>
                <Switch
                    checked={orgEnabled}
                    disabled={systemOverrideLocked || mutation.isPending}
                    loading={mutation.isPending}
                    onChange={(next) => mutation.mutate({ enabled: next })}
                />
            </div>
        </Card>
    );
}

type McpQuickFilter = 'all' | 'granted' | 'not-granted';

const MCP_QUICK_FILTER_CHIPS: QuickFilterChipConfig<McpQuickFilter>[] = [
    { key: 'all', label: 'All' },
    { key: 'granted', label: 'Granted', icon: <Check size={14} />, color: 'green' },
    { key: 'not-granted', label: 'Not granted', icon: <Ban size={14} />, color: 'red' },
];

function UserAccessTable({ orgEffective }: { orgEffective: boolean }) {
    const { trpc, trpcUtils } = useTrpc();
    const [quickFilter, setQuickFilter] = useState<McpQuickFilter>('all');
    const [queryParams, setQueryParams] = useState<RouterInputs['mcpAccess']['getUserAccessList']>({
        limit: DEFAULT_PAGE_SIZE,
    });
    const userAccessQuery = trpc.mcpAccess.getUserAccessList.useQuery(queryParams);
    const { data, error, isFetching } = userAccessQuery;

    const mutation = trpc.mcpAccess.setUserAccess.useMutation({
        async onSuccess(_data, variables) {
            message.success(
                variables.enabled ? 'MCP access enabled for the user.' : 'MCP access disabled for the user.',
            );
            await trpcUtils.mcpAccess.getUserAccessList.invalidate();
        },
        onError(error) {
            errorMessage.show(error);
        },
    });

    const bulkMutation = trpc.mcpAccess.setUserAccessBulk.useMutation({
        async onSuccess(result, variables) {
            const count = result.updatedCount;
            message.success(
                variables.enabled
                    ? `MCP access granted to ${count} user${count === 1 ? '' : 's'}.`
                    : `MCP access revoked from ${count} user${count === 1 ? '' : 's'}.`,
            );
            await trpcUtils.mcpAccess.getUserAccessList.invalidate();
        },
        onError(error) {
            errorMessage.show(error);
        },
    });

    type UserRow = NonNullable<typeof data>['rows'][number];

    const columns = [
        {
            key: 'name',
            title: 'User',
            render: (record: UserRow) =>
                [record.firstName, record.lastName].filter(Boolean).join(' ') || record.email || '—',
        },
        {
            key: 'email',
            title: 'Email',
            render: (record: UserRow) => record.email ?? '—',
        },
        {
            key: 'status',
            title: 'Status',
            width: 120,
            render: (record: UserRow) => (
                <Badge
                    status={record.disabled ? 'error' : 'success'}
                    text={
                        <span
                            className={
                                record.disabled
                                    ? 'font-medium text-red-600 dark:text-red-400'
                                    : 'font-medium text-green-600 dark:text-green-400'
                            }
                        >
                            {record.disabled ? 'Disabled' : 'Active'}
                        </span>
                    }
                />
            ),
        },
        {
            key: 'mcpEnabled',
            title: 'MCP access',
            width: 140,
            render: (record: UserRow) => {
                const isThisRowPending = mutation.isPending && mutation.variables?.userId === record.userId;
                return (
                    <Switch
                        checked={record.mcpEnabled}
                        disabled={!orgEffective || record.disabled || isThisRowPending}
                        loading={isThisRowPending}
                        onChange={(next) => mutation.mutate({ userId: record.userId, enabled: next })}
                    />
                );
            },
        },
    ];

    const tableProps = usePrismaTable({
        data: data?.rows ?? [],
        total: data?.total ?? 0,
        loading: isFetching,
        columns,
        rowKey: (row) => String(row.userId),
        queryParams,
        onParamsChange: setQueryParams,
        // The access chips filter through `mcpEnabled`, outside `where`.
        emptyState: {
            hasActiveFilters: !isWhereClauseEmpty(queryParams.where) || queryParams.mcpEnabled !== undefined,
            noRecordsTitle: 'No users yet',
        },
        pagination: { position: 'both', showSizeChanger: true },
        refreshable: { enabled: true, onRefresh: () => userAccessQuery.refetch() },
        rowSelection: {
            enabled: true,
            type: 'checkbox',
            preserveSelectedRowKeys: true,
            // Mirror the per-row Switch: disabled users (and the whole table when the org is not
            // effectively enabled) can't be granted access, so they can't be batch-selected either.
            getCheckboxProps: (row) => ({ disabled: !orgEffective || row.disabled }),
        },
    });

    const selectedCount = tableProps.selectedRowKeys.length;

    const handleQuickFilterChange = (next: McpQuickFilter) => {
        setQuickFilter(next);
        setQueryParams((prev) => ({
            ...prev,
            mcpEnabled: match(next)
                .with('all', () => undefined)
                .with('granted', () => true)
                .with('not-granted', () => false)
                .exhaustive(),
            offset: 0,
        }));
    };

    const handleBulkAccess = (enabled: boolean) => {
        bulkMutation.mutate(
            { userIds: tableProps.selectedRowKeys.map((key) => BigInt(String(key))), enabled },
            { onSuccess: () => tableProps.clearSelection() },
        );
    };

    return (
        <div>
            <Typography.Title level={5}>Per-user access</Typography.Title>
            {error && (
                <InlineQueryError
                    compact
                    className="mb-4"
                    error={error}
                    onRetry={() => void userAccessQuery.refetch()}
                />
            )}
            {!orgEffective && (
                <Alert
                    type="warning"
                    showIcon
                    className="mb-4"
                    message="Enable MCP access for the organisation above before granting access to individual users."
                />
            )}
            <QuickFilterChips
                chips={MCP_QUICK_FILTER_CHIPS}
                value={quickFilter}
                onChange={handleQuickFilterChange}
                aria-label="Filter users by MCP access"
            />
            <TableToolbar
                filters={
                    <UserFilters
                        urlKey="mcp-user-filters"
                        fieldsToHide={{
                            userId: true,
                            phone: true,
                            position: true,
                            systemUsage: true,
                            canViewUnallocatedClaims: true,
                            isEmployee: true,
                            createdDate: true,
                            lastLogin: true,
                            lastEmailStatus: true,
                        }}
                        onChange={(where) => {
                            setQueryParams((prev) => ({ ...prev, where, offset: 0 }));
                        }}
                    />
                }
                actions={
                    <>
                        <Button
                            type="secondary"
                            disabled={!orgEffective || !selectedCount || bulkMutation.isPending}
                            loading={bulkMutation.isPending && bulkMutation.variables?.enabled === true}
                            onClick={() => handleBulkAccess(true)}
                        >
                            Grant access{selectedCount > 0 ? ` (${selectedCount})` : ''}
                        </Button>
                        <Button
                            type="secondary"
                            disabled={!orgEffective || !selectedCount || bulkMutation.isPending}
                            loading={bulkMutation.isPending && bulkMutation.variables?.enabled === false}
                            onClick={() => handleBulkAccess(false)}
                        >
                            Revoke access{selectedCount > 0 ? ` (${selectedCount})` : ''}
                        </Button>
                    </>
                }
            />
            {!error && <PrismaTable {...tableProps} size="small" />}
        </div>
    );
}
